import dayjs from 'dayjs';

import { type Reading, dateError, numeric } from '../ContributionMargin/liveModel';
import { customPrior } from '../ContributionMargin/sourceWindows';

export const salesDateControls = {
  order_date_from: ['Order date from · UTC (optional)', 'date'],
  order_date_to: ['Order date to · UTC (optional)', 'date'],
  transaction_from: ['Transaction from · Sydney (optional)', 'datetime-local'],
  transaction_to: ['Transaction to · Sydney (optional)', 'datetime-local'],
} as const;
export const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function marketingPrior(from: string, to: string, comparison: string, priorFrom: string, priorTo: string) {
  if (comparison === 'custom' && (priorFrom || priorTo)) return { from: priorFrom, to: priorTo };
  // The workbook shifts each LY bound independently, including leap-day clamping.
  if (comparison === 'year' || comparison === 'custom')
    return {
      from: dayjs(from).subtract(1, 'year').format('YYYY-MM-DD'),
      to: dayjs(to).subtract(1, 'year').format('YYYY-MM-DD'),
    };
  return customPrior(from, to, comparison);
}
export function marketingDateError(params: URLSearchParams, sales: boolean, comparison: string): string | null {
  if (comparison === 'custom' && (params.has('priorFrom') || params.has('priorTo'))) {
    const error = dateError(params.get('priorFrom') ?? '', params.get('priorTo') ?? '');
    if (error) return 'Choose valid custom comparison dates with From on or before To.';
  }
  if (!sales) return null;
  for (const [from, to, dateOnly] of [
    ['order_date_from', 'order_date_to', true],
    ['transaction_from', 'transaction_to', false],
  ] as const) {
    const start = params.get(from) ?? '',
      end = params.get(to) ?? '';
    const valid = (s: string) =>
      !s ||
      (dateOnly
        ? !dateError(s, s)
        : !dateError(s.slice(0, 10), s.slice(0, 10)) &&
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s) &&
          dayjs(s).isValid() &&
          dayjs(s).format(s.length === 16 ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DDTHH:mm:ss') === s);
    if (!valid(start) || !valid(end) || (start && end && start > end))
      return 'Choose valid optional Sales bounds with From on or before To.';
  }
  return null;
}
/** Native selected-population share corresponding to the Sales All GM AR PctTotal measure. */
export function salesGmarShares(rows: Array<Reading>): Array<Reading> {
  const amounts = rows.map((row) => numeric(row.sales_gm_ar));
  const total = amounts.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  return rows.map((row, index) => ({
    ...row,
    sales_gm_ar_share: total === 0 || amounts[index] === null ? null : amounts[index]! / total,
  }));
}
